import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useMarket } from "@/lib/market";
import { dateTime } from "@/lib/fmt";
import { PageHead } from "@/components/Layouts";
import { Loading, Section, StatusPill } from "@/components/Bits";

const ok = (p, m) => p.then(() => toast.success(m)).catch((e) => toast.error(errMsg(e)));

function Pages() {
  const [pages, setPages] = useState(null); const [cur, setCur] = useState(null);
  const load = useCallback(() => api.get("/admin/content").then((r) => setPages(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!pages) return <Loading />;
  return (
    <Section title="Pages & policies" testid="content-pages">
      <div className="flex flex-wrap gap-1.5 mb-3">{pages.map((p) => <button key={p.slug} className={cur?.slug === p.slug ? "btn-primary btn-sm" : "btn-ghost btn-sm"} onClick={() => setCur(p)} data-testid={`content-edit-${p.slug}`}>{p.title} {p.status === "draft" && "·draft"}</button>)}</div>
      {cur && <div className="space-y-2"><input className="inp" value={cur.title} onChange={(e) => setCur({ ...cur, title: e.target.value })} aria-label="Title" /><textarea className="inp !min-h-[260px]" value={cur.body} onChange={(e) => setCur({ ...cur, body: e.target.value })} aria-label="Body" data-testid="content-body-input" />
        <select className="inp !w-auto" value={cur.status} onChange={(e) => setCur({ ...cur, status: e.target.value })} aria-label="Status"><option value="draft">Draft — pending operator review</option><option value="operator_reviewed">Reviewed by operator</option></select>
        <button className="btn-primary" onClick={() => ok(api.put(`/admin/content/${cur.slug}`, cur), "Saved").then(load)} data-testid="content-save-btn">Save page</button></div>}
    </Section>
  );
}

function Taxonomy() {
  const { meta } = useMarket(); const [c, setC] = useState({ slug: "", name: "", theme: "services" }); const [city, setCity] = useState({ name: "", state: "" });
  return (
    <Section title="Categories & cities">
      <p className="text-sm mb-2">{meta.categories.map((x) => x.name).join(" · ")}</p>
      <div className="grid sm:grid-cols-4 gap-2"><input className="inp" placeholder="slug" value={c.slug} onChange={(e) => setC({ ...c, slug: e.target.value })} /><input className="inp" placeholder="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} />
        <select className="inp" value={c.theme} onChange={(e) => setC({ ...c, theme: e.target.value })} aria-label="Theme">{["cafe", "retail", "digital", "manufacturing", "wellness", "services"].map((t) => <option key={t}>{t}</option>)}</select><button className="btn-ghost" onClick={() => ok(api.post("/admin/categories", c), "Category saved (refresh to see)")}>Save category</button></div>
      <p className="text-sm mt-4 mb-2">{meta.cities.map((x) => x.name).join(" · ")}</p>
      <div className="grid sm:grid-cols-3 gap-2"><input className="inp" placeholder="City" value={city.name} onChange={(e) => setCity({ ...city, name: e.target.value })} /><input className="inp" placeholder="State" value={city.state} onChange={(e) => setCity({ ...city, state: e.target.value })} /><button className="btn-ghost" onClick={() => ok(api.post("/admin/cities", city), "City saved (refresh to see)")}>Save city</button></div>
    </Section>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const n = (k, t) => <div><label className="lbl">{t}</label><input className="inp" type="number" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></div>;
  return (
    <Section title="Demo mode & retention" testid="settings-section">
      <label className="flex gap-2 items-center text-sm mb-4"><input type="checkbox" checked={!!s.demo_mode} onChange={(e) => setS({ ...s, demo_mode: e.target.checked })} data-testid="demo-mode-toggle" />Show demo listings publicly (always labelled; always excluded from live reports)</label>
      <div className="grid sm:grid-cols-4 gap-3">{n("retention_closed_accounts_days", "Closed accounts (days)")}{n("retention_messages_days", "Messages (days)")}{n("retention_documents_days", "Documents (days)")}{n("retention_audit_days", "Audit log (days)")}</div>
      <p className="text-[11px] text-stone-500 mt-2">Retention values are recorded policy settings for operator review; automated purging is not enabled.</p>
      <button className="btn-primary mt-3" onClick={() => ok(api.put("/admin/settings", s), "Settings saved")} data-testid="settings-save-btn">Save settings</button>
    </Section>
  );
}

function Nda() {
  const [t, setT] = useState(null); const [body, setBody] = useState("");
  const load = useCallback(() => api.get("/admin/nda-templates").then((r) => { setT(r.data); setBody(r.data[0]?.body || ""); }), []);
  useEffect(() => { load(); }, [load]);
  if (!t) return null;
  return (
    <Section title="NDA template (versioned)" testid="nda-template">
      <p className="text-xs text-stone-500 mb-2">Active: v{t[0]?.version} · placeholders {"{{buyer_name}}"}, {"{{seller_name}}"}, {"{{business}}"}. Saving creates a new version; past acceptances keep their exact text.</p>
      <textarea className="inp !min-h-[200px]" value={body} onChange={(e) => setBody(e.target.value)} aria-label="NDA text" />
      <button className="btn-primary mt-2" onClick={() => ok(api.post("/admin/nda-templates", { body, title: "Confidentiality agreement" }), "New NDA version active").then(load)} data-testid="nda-save-btn">Publish new version</button>
      <p className="text-xs mt-2">{t.map((x) => `v${x.version} ${dateTime(x.created_at)}${x.active ? " (active)" : ""}`).join(" · ")}</p>
    </Section>
  );
}

function Notify() {
  const [n, setN] = useState({ role: "buyer", title: "", body: "" });
  return (
    <Section title="In-app notification">
      <div className="grid sm:grid-cols-3 gap-2"><select className="inp" value={n.role} onChange={(e) => setN({ ...n, role: e.target.value })} aria-label="Audience">{["buyer", "seller", "reviewer", "admin"].map((r) => <option key={r}>{r}</option>)}</select><input className="inp" placeholder="Title" value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} /><input className="inp" placeholder="Message" value={n.body} onChange={(e) => setN({ ...n, body: e.target.value })} /></div>
      <button className="btn-ghost mt-2" onClick={() => ok(api.post("/admin/notifications", n), "Sent in-app")}>Send in-app notice</button>
      <p className="text-[11px] text-stone-500 mt-1">In-app only. Bulk email is intentionally not offered.</p>
    </Section>
  );
}

export default function AdminContent() {
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Admin" title="Content & settings" />
      <div className="flex gap-2"><StatusPill s="draft" /><span className="text-xs text-stone-500">Policy pages remain drafts until the operator reviews them. No legal approvals are implied.</span></div>
      <Pages /><Nda /><Settings /><Taxonomy /><Notify />
    </div>
  );
}
